import { SkyDialog, SkyMessageBar, SkyText } from "@skypointcloud/skypoint-ui";
import { useState, type ReactNode } from "react";
import { SingleCheckbox } from "@/components/ui/SingleCheckbox";
import { stageRememberBrowser } from "@/lib/auth/mfaStepUp";

/** Admin MFA: the dialog's copy when the server asked for a second sign-in step. */
export const MFA_STEP_COPY = {
  title: "A second sign-in step is needed",
  description:
    "Your organization requires a second sign-in step, such as a code sent to your email, for this action. Signing in again asks your sign-in provider for it.",
} as const;

/**
 * Privileged re-authentication (canvas 08 block 04; FR-14, SEC-4).
 *
 * The session is STILL VALID here. What the server refused is the token's
 * `auth_time`, not its expiry, so the first thing this dialog does is explain why
 * a working session is not enough — a modal that offered only a button would read
 * as a bug, and the user would cancel out of a save that had not failed.
 *
 * Nothing has been written when this opens: no column, no vault entry. The form
 * behind it keeps every value, and the save continues by itself once identity is
 * confirmed, which is the behaviour the acceptance test pins.
 *
 * The copy says "may take you to your sign-in page" because on the MSAL path it
 * does: `acquireTokenRedirect` leaves the document entirely. The draft and the
 * held body are carried across that redirect by `lib/auth/reauthHandoff`, so the
 * promise holds on both paths — but promising the user they will stay on this
 * page would be a promise only the dev provider keeps.
 */
export function ReauthDialog({
  open,
  isRetrying,
  changeSummary,
  error,
  onConfirm,
  onCancel,
  title = "Confirm your identity to change the destination",
  description = "Your session is still valid, but you last signed in more than five minutes ago. Changing where this instance lands data needs a fresh sign-in.",
  heldMessage = "Your changes are held for this save. Signing in again may take you to your sign-in page. skyData brings you back here and finishes the save by itself.",
  footnote = "No column was written and no credential reached the vault. Cancelling leaves the form exactly as it is so you can save later.",
  heldTitle = "Nothing has been saved yet",
  cancelLabel = "Cancel and keep editing",
  code = "REAUTH_REQUIRED",
  blocked = false,
  remember,
}: {
  open: boolean;
  isRetrying: boolean;
  /** What the save will do once identity is confirmed, in the user's own terms. */
  changeSummary?: ReactNode;
  /**
   * Why the last attempt got nowhere. Rendered rather than swallowed: a button
   * that spins and then does nothing reads as a broken dialog, and the user
   * presses it again.
   */
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** The dialog's title; the destination save's by default. */
  title?: string;
  /** The dialog's description; the destination save's by default. */
  description?: string;
  /** What happens to the held change across a sign-in; the destination save's by default. */
  heldMessage?: string;
  /** The closing line; the destination save's by default. */
  footnote?: string;
  /** The held message's title; "Nothing has been saved yet" by default (#1110: an export's differs). */
  heldTitle?: string;
  /**
   * The Cancel button's label; "Cancel and keep editing" by default, for a form. #1110: the
   * one-click changes and the exports, which have no form, say "Cancel".
   */
  cancelLabel?: string;
  /**
   * Admin MFA: which refusal opened the dialog. `MFA_REQUIRED` swaps the title and description
   * for the second-step copy and offers "Do not ask again on this browser".
   */
  code?: "REAUTH_REQUIRED" | "MFA_REQUIRED";
  /**
   * The provider already came back without a second factor for this request: the dialog says why
   * (in `error`) and offers no second redirect (design 4.3, "No loop").
   */
  blocked?: boolean;
  /** Offer "Do not ask again on this browser for N days"; absent or 0 days hides it. */
  remember?: { days: number; account: string | null };
}) {
  const [rememberBrowser, setRememberBrowser] = useState(false);
  const isMfa = code === "MFA_REQUIRED";
  const confirm = () => {
    if (isMfa && rememberBrowser && remember) stageRememberBrowser(remember.account);
    onConfirm();
  };
  return (
    <SkyDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
      onBeforeClose={(reason) =>
        // Signing in again does not on its own finish this dialog: the attempt
        // can fail, and it must then stay on screen carrying its reason rather
        // than vanishing and leaving the user to guess. The SAVE closes it, by
        // turning `open` off once the write has landed. Every other way out —
        // Cancel, Escape, the backdrop — is the user saying they are done.
        reason !== "primaryAction"
      }
      size="medium"
      testId="reauth-dialog"
      title={isMfa ? MFA_STEP_COPY.title : title}
      description={isMfa ? MFA_STEP_COPY.description : description}
      {...(blocked
        ? {}
        : { primaryAction: { label: "Sign in again", onClick: confirm, loading: isRetrying } })}
      secondaryAction={{ label: cancelLabel, onClick: onCancel }}
    >
      <div className="space-y-4">
        {error ? (
          <SkyMessageBar
            intent="error"
            layout="multiline"
            testId="reauth-error"
            title={blocked ? "No second step was asked for" : "We could not confirm your identity"}
            message={error}
          />
        ) : null}
        {isMfa && !blocked && remember && remember.days > 0 ? (
          <SingleCheckbox
            label={`Do not ask again on this browser for ${remember.days} days`}
            checked={rememberBrowser}
            onChange={setRememberBrowser}
            testId="reauth-remember-browser"
          />
        ) : null}
        <SkyMessageBar intent="info" layout="multiline" title={heldTitle} message={heldMessage} />
        {changeSummary ? <div data-testid="reauth-change-summary">{changeSummary}</div> : null}
        <SkyText as="p" size={200} color="muted">
          {footnote}
        </SkyText>
      </div>
    </SkyDialog>
  );
}
